import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import {
  Button,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Visible,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  useApi,
  Suspense,
  ForbiddenError,
  useApiWithErrors,
  CookieUtil,
  ApiError,
} from '@axiom/ui';
import qs from 'qs';
import type { LimitedCandidate } from '@axiom/validation';

import type { TalentSearchActiveQueryFilters } from '../../schema/talent-search-active-query-filters';
import { WindowUtil } from '../../utils/window-util';
import { isAFCUser, isEnvoyUser, isLpUser } from '../../utils/roles-util';
import { TalentDetails } from '../TalentDetails/TalentDetails';
import { NearestNeighbor } from '../NearestNeighbor/NearestNeighbor';
import { CandidateApi } from '../../api/candidate-api';
import { LimitedCandidatesApi } from '../../api/limited-candidates-api';
import { CandidateLegalTechSkillsApi } from '../../api/candidate-legal-tech-skills-api';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { PublicRefIdSlugUtil } from '../../utils/public-ref-id-slug-util';
import { UserApi } from '../../api/user-api';
import { localStorageUtil } from '../../utils/local-storage-util';
import { ExternalSearchReachOutModal } from '../ExternalSearchReachOutModal/ExternalSearchReachOutModal';
import { ContactAxiomModal } from '../ContactAxiomModal/ContactAxiomModal';
import { TalentSearchOptions } from '../TalentSearchOptions/TalentSearchOptions';

import { SinglePageViewDisclaimer } from './SinglePageViewDisclaimer';

const altUrl = '/browse-talent';

export const SinglePageView = () => {
  const isUserLoggedIn = CookieUtil.hasUserCookie() || null;
  const [{ data: user } = { data: null }] = useApi(
    isUserLoggedIn ? UserApi.readUser() : undefined
  );
  const [searchParams] = useSearchParams();
  const isAFCUserAuthed = user && isAFCUser(user);
  const isLPUserAuthed = user && isLpUser(user);
  const navigate = useNavigate();
  const { id } = useParams();
  const idOrPublicRefId = PublicRefIdSlugUtil.sanitizeId(id);
  const shouldCallCandidatesApi = id && idOrPublicRefId ? true : undefined;
  const activeFilters =
    WindowUtil.getUrlQuery() as TalentSearchActiveQueryFilters;

  const [response] = useApiWithErrors(
    shouldCallCandidatesApi
      ? LimitedCandidatesApi.readLimitedCandidate(idOrPublicRefId ?? '')
      : undefined
  );

  const [
    showHubspotExternalReachOutModal,
    setShowHubspotExternalReachOutModal,
  ] = useState(false);
  const [inquiredCandidate, setInquiredCandidate] =
    useState<LimitedCandidate | null>(null);
  const [isPricingShown, setIsPricingShown] = useState(
    !!localStorageUtil.showPricing().get()
  );

  const candidate = response instanceof ApiError ? undefined : response?.data;
  const isRestricted =
    candidate && 'restricted' in candidate ? candidate.restricted : false;

  const [
    { data: taxonomy },
    { data: groupedExperiences } = { data: [] },
    { data: legalTechSkills } = { data: [] },
  ] = useApi(
    TaxonomyApi.readTaxonomy(),
    candidate && (isAFCUserAuthed || isLPUserAuthed) && !isRestricted
      ? CandidateApi.readCandidateGroupedExperiences(candidate.id)
      : undefined,
    candidate && (isAFCUserAuthed || isLPUserAuthed) && !isRestricted
      ? CandidateLegalTechSkillsApi.readLegalTechSkills(candidate.id)
      : undefined
  );

  if (response instanceof ApiError) {
    if (response instanceof ForbiddenError) {
      WindowUtil.toLogin();
      return null;
    }
    throw response;
  }

  const navigateToALT = () => {
    void navigate(altUrl);
  };

  const allTalentUrl = () => {
    const { page, ...filters } = Object.fromEntries(searchParams);
    return `../page/${page ?? 1}${qs.stringify(filters, { addQueryPrefix: true })}`;
  };

  return candidate ? (
    <>
      {showHubspotExternalReachOutModal ? (
        <ExternalSearchReachOutModal
          onClose={() => {
            setShowHubspotExternalReachOutModal(false);
          }}
          activeFilters={activeFilters}
          user={user}
        />
      ) : null}
      {inquiredCandidate ? (
        <ContactAxiomModal
          candidate={inquiredCandidate}
          onClose={() => {
            setInquiredCandidate(null);
          }}
        />
      ) : null}
      <Grid stackable name="SINGLE_PAGE_VIEW">
        <GridRow>
          <GridColumn smallScreenWidth={12} largeScreenWidth={8}>
            {!!isAFCUserAuthed && (
              <Gutter bottom="16px">
                <Layout position="middle">
                  <LayoutItem fluid>
                    <Button
                      to={allTalentUrl()}
                      pattern="primary"
                      variation="minimal"
                      name="ALL_TALENT"
                      icon="arrow-left"
                      iconPosition="left"
                    >
                      All Talent
                    </Button>
                  </LayoutItem>
                  {isEnvoyUser(user) && (
                    <LayoutItem>
                      <TalentSearchOptions
                        updateState={() => {
                          setIsPricingShown(prev => !prev);
                        }}
                      />
                    </LayoutItem>
                  )}
                </Layout>
              </Gutter>
            )}
            <TalentDetails
              candidate={candidate}
              groupedExperiences={groupedExperiences}
              user={user}
              taxonomy={taxonomy}
              legalTechSkills={legalTechSkills}
              setInquiredCandidate={setInquiredCandidate}
              isPricingShown={isPricingShown}
            />
            <Gutter bottom="16px" />
            <Visible only="largeScreen">
              <SinglePageViewDisclaimer />
            </Visible>
          </GridColumn>
          <GridColumn smallScreenWidth={12} largeScreenWidth={4}>
            {!!isAFCUserAuthed && !candidate.isViewOnly && (
              <Suspense>
                <Gutter bottom="16px" name="NEAREST_NEIGHBOR_CONTAINER">
                  <NearestNeighbor
                    handleReachOutClick={() => {
                      setShowHubspotExternalReachOutModal(true);
                    }}
                    candidate={candidate}
                  />
                </Gutter>
              </Suspense>
            )}
            <Visible only="smallScreen">
              <SinglePageViewDisclaimer />
            </Visible>
          </GridColumn>
        </GridRow>
      </Grid>
    </>
  ) : (
    <Modal name="LINK_NOT_WORKING">
      <ModalHeader name="LINK_NOT_WORKING_HEADER" onClose={navigateToALT}>
        Link not working?
      </ModalHeader>
      <ModalSection>
        That profile may no longer be available, or you need to sign in to
        access it. <br />
        Here are a few things you can do:
        <ul>
          <li>
            Sign in and sign up for an Axiom account and try the link again.
          </li>
          <li>
            If someone sent you the link, ask if they can share the profile with
            you a different way.
          </li>
          <li>
            Visit Access Legal Talent to find similar profiles, explore more
            Axiom talent, or chat with an Axiom expert.
          </li>
        </ul>
      </ModalSection>
      <ModalFooter>
        <Button
          variation="outline"
          name="ACCESS_LEGAL_TALENT_BUTTON"
          onClick={navigateToALT}
        >
          Access Legal Talent
        </Button>
        <Button
          name="SIGN_IN_SIGN_UP_BUTTON"
          onClick={() => {
            WindowUtil.toLogin();
          }}
        >
          Sign in or Sign up
        </Button>
      </ModalFooter>
    </Modal>
  );
};
